import { TokenInterface } from '@/core/tokens';
import { useNetwork } from '@/presentation/context/network';
import Link from 'next/link';
import { Address, Collection, LiquidityPool, Vault, Zero } from 'nftx.js';
import { useTranslations } from 'use-intl';
import Price from '../../elements/Price';
import { UnderlineTabs } from '../../modules/Tabs';
import { ReactNode, useEffect, useMemo } from 'react';
import { useSpringRef, useTransition, animated } from '@react-spring/web';
import Icon from '../../elements/Icon/Icon';
import { Icons } from '../../elements/Icon';
import { Chain } from 'viem';
import {
  getBestBuyPrice,
  getBestSellPrice,
} from '@/application/read/vaults/utils';
import { enDash } from '@/presentation/utils/text';
import Tooltip from '@/presentation/components/ui/Tooltip';
import VaultSwitcher from '../../modules/VaultSwitcher';
import Button from '../../elements/Button';
import ChoiceButton from '../../elements/ChoiceButton';
import { CreatePositionModalProps } from '@/presentation/context/modal/ModalContext';
import ellipsis from '@/presentation/utils/ellipsis';
import Copy from '../../elements/Copy';
import VaultIcon from '../../elements/VaultIcon';

export interface CollectionProps extends Partial<Collection> {
  chain: Chain;
  collectionAddress?: Address;
  vaults?: Vault[];
  tokens?: TokenInterface[];
  pools?: LiquidityPool[];
  floorPrice?: bigint;
  tabs: [buy: ReactNode, sell: ReactNode, info: ReactNode];
  tab: CollectionTab;
  onTabChange: (t: CollectionTab) => void;
  selectedVault: Vault | undefined;
  onSelectVault: (vault: Vault | undefined) => void;
  onAddToPool: (props: CreatePositionModalProps) => void;
  onBuyToken: () => void;
}

export type CollectionTab = 'buy' | 'your-items' | 'info';

const Collection = ({
  chain,
  collectionAddress,
  image,
  name,
  tabs,
  floorPrice,
  vaults = [],
  pools,
  tokens,
  twitterUsername,
  externalUrl,
  onTabChange,
  tab,
  selectedVault,
  onSelectVault,
  onAddToPool,
  onBuyToken,
}: CollectionProps) => {
  const availableTabs = useMemo((): CollectionTab[] => {
    return ['buy', 'your-items', 'info'];
  }, []);
  const { slug } = useNetwork();
  const t = useTranslations('Collection');

  const transRef = useSpringRef();

  const index = availableTabs.indexOf(tab);

  useEffect(() => {
    transRef.start();
  }, [transRef, index]);

  const transitions = useTransition(index, {
    ref: transRef,
    keys: null,
    from: {
      opacity: 0,
    },
    enter: {
      opacity: 1,
    },
    leave: {
      opacity: 0,
      position: 'absolute', // the departing item doesn't affect layout
    },
  });

  const hasMultipleVaults = (vaults || []).length > 1;

  const bestBuyPrice = useMemo(() => {
    if (selectedVault) {
      return selectedVault.prices[0].redeem.price;
    }
    return getBestBuyPrice(vaults);
  }, [vaults, selectedVault]);

  const bestSellPrice = useMemo(() => {
    if (selectedVault) {
      return selectedVault.prices[0].mint.price;
    }
    return getBestSellPrice(vaults);
  }, [selectedVault, vaults]);

  const volume24 = useMemo(() => {
    let relevantPools = pools ?? [];
    if (selectedVault) {
      relevantPools = relevantPools.filter(
        (pool) => pool.vaultId === selectedVault.vaultId
      );
    }
    return relevantPools.reduce((total, pool) => {
      return total + pool.dailyVolume;
    }, Zero);
  }, [pools, selectedVault]);

  const volume7d = useMemo(() => {
    let relevantPools = pools ?? [];
    if (selectedVault) {
      relevantPools = relevantPools.filter(
        (pool) => pool.vaultId === selectedVault.vaultId
      );
    }
    return relevantPools.reduce((total, pool) => {
      return total + pool.weeklyVolume;
    }, Zero);
  }, [pools, selectedVault]);

  return (
    <div className="min-h-full space-y-6">
      <nav className="rounded-sm bg-gray-300 bg-opacity-70 text gray-800 font-medium uppercase px-2 py-1 inline-flex items-center space-x-2 text-xs">
        <Link href={`/${slug}/collections`} className="hover:underline">
          {t('nav.collections')}
        </Link>
        <span>{'>'}</span>
        <span>{name}</span>
      </nav>
      <header className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <div className="lg:flex overflow-hidden">
          <div className="flex mr-8 min-w-[25%] mb-8 lg:mb-0">
            {image ? (
              <img
                src={image}
                alt={name}
                className="w-24 rounded-lg overflow-hidden mr-4 flex-none object-cover"
              />
            ) : (
              <span className="w-20 h-20 rounded-lg bg-blue-500 mr-4" />
            )}
            <div className="space-y-2">
              <h1 className="text-gray-800 font-black text-2xl leading-tight">
                {name}
              </h1>
              <div className="text-gray-500 text-sm flex items-center">
                <a
                  href={`${chain.blockExplorers?.default.url}/address/${collectionAddress}`}
                  className="hover:underline font-medium mr-2"
                >
                  {ellipsis(collectionAddress)}
                </a>
                <Copy
                  size={16}
                  copyText={`${collectionAddress}`}
                  className="inline-flex"
                />
              </div>
              <div className="flex">
                {hasMultipleVaults && (
                  <VaultSwitcher
                    selected={selectedVault}
                    vaults={vaults}
                    setSelected={onSelectVault}
                  />
                )}
                {selectedVault?.eligibilityModule?.id && (
                  <Icon
                    name={Icons.TICKET}
                    size={24}
                    className="ml-2 inline-flex my-auto text-pink-500"
                  />
                )}
              </div>
            </div>
          </div>
          <dl className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 gap-4">
            {[
              {
                label: 'price',
                value: (
                  <span>
                    {bestBuyPrice > 0 ? <Price value={bestBuyPrice} /> : '-'}
                  </span>
                ),
              },
              {
                label: 'bestSellPrice',
                value: (
                  <span>
                    {bestSellPrice > 0 ? <Price value={bestSellPrice} /> : '—'}
                  </span>
                ),
              },
              {
                label: 'floorPrice',
                value:
                  floorPrice && floorPrice > 0 ? (
                    <Price value={floorPrice} />
                  ) : (
                    '-'
                  ),
              },
              {
                label: 'volume24h',
                value: volume24 ? <Price value={volume24} /> : '-',
              },
              {
                label: 'volume7d',
                value: volume7d ? <Price value={volume7d} /> : '-',
              },
            ].map((x, i) => (
              <div
                key={x.label}
                className={`flex-1 ${
                  i === 0
                    ? 'bg-gray-200 border border-gray-300 rounded-md p-3'
                    : ''
                }`}
              >
                <dt className="font-medium text-base text-gray-500">
                  {t(`header.${x.label}`)}
                </dt>
                <dd className="font-bold text-xl">{x.value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="sm:flex items-center mt-4 text-sm flex-wrap">
          {(
            [
              {
                url: `${chain.blockExplorers?.default.url}/address/${collectionAddress}`,
                key: 'blockExplorer',
              },
              {
                url: externalUrl,
                key: 'website',
              },
              {
                url: twitterUsername
                  ? `https://x.com/${twitterUsername}`
                  : null,
                key: 'twitter',
              },
            ] as const
          ).map(
            ({ key, url }) =>
              !!url && (
                <a
                  href={url}
                  key={key}
                  className="inline-flex items-center font-medium text-gray-500 hover:underline mr-4 mb-2 sm:mb-0"
                  target="_blank"
                  rel="nofollow noreferrer"
                >
                  {t(`meta.${key}`, {
                    blockExplorer: chain.blockExplorers?.default.name,
                  })}
                  <Icon name={Icons.EXTERNAL} size={16} className="ml-1" />
                </a>
              )
          )}
          <div className="sm:ml-auto flex whitespace-nowrap mb-2 sm:mb-0 space-x-4">
            {selectedVault || vaults.length === 1 ? (
              <Button variant="tertiary" size="small" onClick={onBuyToken}>
                <VaultIcon
                  vaultId={selectedVault?.vaultId || vaults[0].vaultId}
                  className="rounded-full w-5 h-5 mr-2"
                />
                {`Buy $${
                  selectedVault?.token.symbol || vaults[0].token.symbol
                } token`}
              </Button>
            ) : null}
            {selectedVault ? (
              <Button
                size="small"
                className="w-full"
                onClick={() => onAddToPool({ vaultId: selectedVault.vaultId })}
              >
                {'Add to pool'}
              </Button>
            ) : vaults.length > 0 ? (
              vaults.length === 1 ? (
                <Button
                  size="small"
                  className="w-full"
                  onClick={() => onAddToPool({ vaultId: vaults[0].vaultId })}
                >
                  {'Add to pool'}
                </Button>
              ) : (
                <div className="w-full">
                  <ChoiceButton
                    size="small"
                    options={vaults?.map((x) => ({
                      label: x.token.name,
                      onClick: () => onAddToPool({ vaultId: x.vaultId }),
                    }))}
                  >
                    {'Add to pool'}
                  </ChoiceButton>
                </div>
              )
            ) : null}
          </div>
        </div>
      </header>
      {selectedVault?.eligibilityModule?.id && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl px-6 py-4 font-medium">
          {!!selectedVault.eligibilityModule?.eligibleRange?.length && (
            <dl className="flex space-x-2">
              <dt className="text-gray-500 flex items-center">
                <Icon
                  name={Icons.TICKET}
                  size={24}
                  className="mr-2 inline-flex text-pink-500"
                />
                {'Token ID range:'}
              </dt>
              <dd className="text-gray-800 flex items-center space-x-1">
                <span>
                  {selectedVault.eligibilityModule.eligibleRange?.join(
                    ` ${enDash} `
                  )}
                </span>
                <Tooltip
                  content={
                    <span>
                      {
                        'Only token IDs within this range are allowed into this vault.'
                      }
                    </span>
                  }
                  label={
                    <span>
                      <Icon name={Icons.QUESTION_MARK_CIRCLE} size={20} />
                    </span>
                  }
                />
              </dd>
            </dl>
          )}
          {!!selectedVault.eligibilityModule?.eligibleIds?.length && (
            <dl className="flex space-x-2">
              <dt className="text-gray-500 flex items-center">
                <Icon
                  name={Icons.TICKET}
                  size={24}
                  className="mr-2 inline-flex text-pink-500"
                />
                {'Token IDs allowed:'}
              </dt>
              <dd className="text-gray-800 flex items-center space-x-1">
                <span>
                  {selectedVault.eligibilityModule.eligibleIds.join(`, `)}
                </span>
                <Tooltip
                  content={
                    <span>
                      {'Only the listed token IDs are allowed into this vault.'}
                    </span>
                  }
                  label={
                    <span>
                      <Icon name={Icons.QUESTION_MARK_CIRCLE} size={20} />
                    </span>
                  }
                />
              </dd>
            </dl>
          )}
        </div>
      )}
      <UnderlineTabs
        activeTab={tab}
        direction="horizontal"
        size="large"
        onTabChange={(id) => onTabChange(id as CollectionTab)}
        tabs={[
          {
            id: 'buy',
            label: t('tabs.buy'),
          },
          {
            id: 'your-items',
            label: t('tabs.yourItems'),
          },
          {
            id: 'info',
            label: t('tabs.info'),
          },
        ]}
      />
      <section className="hide-scroll flex-1 flex relative max-w-full">
        {transitions((style, i) => {
          return (
            <animated.div
              style={style}
              key={i}
              className="min-h-full min-w-full max-w-full max-h-full will-change-auto"
            >
              {tabs[i]}
            </animated.div>
          );
        })}
      </section>
    </div>
  );
};

export default Collection;
